'use client';

import { ConsultationService } from '@dobrozem/sdk';
import { Close } from '@radix-ui/react-dialog';
import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import Link from 'next/link';
import { useRef, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { CloseIcon } from '@/assets/react-icons/CloseIcon';
import { cleanPhoneNumber } from '@/utils/common';

// import { sendTelegramChats } from '@/utils/common';
// import { useState } from 'react';
import { Button } from '../Button';
import { Input } from '../Input';
import { Loader } from '../Loader';
import { Modal } from '../Modal';
import { PhoneInput } from '../PhoneInputIntl';
// import { PhoneInput } from '../PhoneInput';
import { StatusMessage } from '../StatusMessage';
import { Text } from '../Text';
import { Textarea } from '../Textarea';
import styles from './ConsultationModal.module.scss';
type Props = {
  className?: string;
  isOpen: boolean;
  onOpenChange: () => void;
};
export const ConsultationModal = ({
  className,
  isOpen,
  onOpenChange
}: Props) => {
  const [isOpenSuccessMessage, setOpenSuccessMessage] = useState<boolean>(false);
  const {
    control,
    handleSubmit,
    reset,
    setError
  } = useForm();
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const [isScrollable, setIsScrollable] = useState(false);
  const consultationMutation = useMutation({
    mutationFn: ConsultationService.createConsultation<false>,
    onSuccess({
      error,
      response
    }) {
      if (response.ok) {
        setOpenSuccessMessage(true);
      }
      if (error) {
        const errorMessage = error as {
          message: string[];
        };
        if (errorMessage.message[0] === 'phone must be a valid phone number') {
          setError('phone', {
            message: 'Неправильний формат номеру телефону'
          });
        }
      }
    }
  });
  const onSubmit = handleSubmit(data => {
    consultationMutation.mutate({
      body: {
        comment: data.message,
        name: data.name,
        phone: cleanPhoneNumber(data.phone)
      }
    });
  });
  const requiredRule = (isRequired: boolean) => ({
    message: 'Обов’язкове поле',
    value: isRequired
  });
  const handleModalClose = () => {
    onOpenChange();
    if (isOpenSuccessMessage) {
      setTimeout(() => {
        setOpenSuccessMessage(false);
        reset();
      }, 500);
    }
  };
  const handleCloseSuccessMessage = () => {
    if (isOpenSuccessMessage) {
      setTimeout(() => {
        setOpenSuccessMessage(false);
        reset();
      }, 500);
    }
  };
  const checkScroll = () => {
    const textarea = textareaRef.current;
    if (textarea) {
      const hasScroll = textarea.scrollHeight > textarea.clientHeight;
      setIsScrollable(hasScroll);
    }
  };
  return <Modal className={clsx(styles.wrapper, className)} onOpenChange={handleModalClose} open={isOpen} data-sentry-element="Modal" data-sentry-component="ConsultationModal" data-sentry-source-file="ConsultationModal.tsx">
      <div className={styles.modal}>
        <Close className={styles.closeBtn} onClick={handleCloseSuccessMessage} data-sentry-element="Close" data-sentry-source-file="ConsultationModal.tsx">
          <CloseIcon data-sentry-element="CloseIcon" data-sentry-source-file="ConsultationModal.tsx" />
        </Close>
        {!isOpenSuccessMessage ? <div className={styles.modalContent}>
            <Text className={styles.title} type='h4'>
              Заявка на консультацію
            </Text>
            <form className={styles.form} onSubmit={onSubmit}>
              <div className={styles.formInputs}>
                <Controller control={control} name='name' render={({
              field: {
                onChange,
                value
              },
              fieldState: {
                error
              }
            }) => {
              return <Input error={error} isRequired onChange={e => onChange(e.target.value.replace(/\s/g, ''))} placeholder={'Імʼя'} value={value} />;
            }} rules={{
              maxLength: {
                message: 'Максимальна довжина імені 50 символів',
                value: 50
              },
              minLength: {
                message: 'Мінімальна довжина імені 2 символи',
                value: 2
              },
              pattern: {
                message: 'Некоректно введено дані',
                value: /^[А-ЯІЇЄҐЁЫЭA-Zа-яіїєґёыэa-z'-]+( [А-ЯІЇЄҐЁЫЭA-Zа-яіїєґёыэa-z'-]+)*$/
              },
              required: requiredRule(true)
            }} />

                <Controller control={control} name='phone' render={({
              field: {
                onChange,
                value
              },
              fieldState: {
                error
              }
            }) => {
              return <PhoneInput error={error} errorClassName={styles.phoneInputError} isRequired onChange={onChange} value={value} />;
            }} rules={{
              // maxLength: {
              //   message: 'Неправильний номер телефону',
              //   value: 19,
              // },
              // minLength: {
              //   message: 'Неправильний номер телефону',
              //   value: 19,
              // },
              // pattern: {
              //   message: 'Неправильний формат номера телефону',
              //   value: /^\+38\s\(\d{3}\)\s\d{3}\s\d{2}\s\d{2}$/,
              // },
              required: requiredRule(true)
            }} />

                <Controller control={control} name='message' render={({
              field: {
                onChange,
                value
              },
              fieldState: {
                error
              }
            }) => {
              return <Textarea error={error} hideLabel={isScrollable} onChange={e => {
                onChange(e);
                checkScroll();
              }} placeholder='Коментар' ref={textareaRef} value={value} />;
            }} rules={{
              maxLength: {
                message: 'Максимальна довжина коментаря 250 символів',
                value: 250
              }
              // required: requiredRule(true),
            }} />
              </div>
              <Button disabled={consultationMutation.isPending} fullWIdth type='submit'>
                Надіслати
              </Button>
              {consultationMutation.isPending && <div className={styles.loaderContainer}>
                  <Loader />
                </div>}
            </form>
            {consultationMutation.error && <Text color='red' type='p6'>
                {consultationMutation.error?.message}
              </Text>}
            <Text className={styles.pivacyText} tag='p' type='p6'>
              Натискаючи на кнопку <span>“Надіслати”</span>, ви погоджуєтесь на збір та обробку
              <Link href={'/privacy-policy'} onClick={onOpenChange}>
                персональних даних
              </Link>
            </Text>
          </div> : <StatusMessage buttonsChildren={<Close onClick={handleCloseSuccessMessage}>
                <Button asDiv fullWIdth>
                  Дякую
                </Button>
              </Close>} description="Заявку на консультацію успішно надіслано! Ми зв'яжемося з вами найближчим часом." logoStatus='smile' />}
      </div>
    </Modal>;
};